/**
 * Token Manager - Gestión de tokens de autenticación
 * SINGLETON: Una sola instancia compartida por todos los servicios
 */

import { authManager } from '../../../utils/authManager';
import type { User } from '../types/auth';

export class TokenManager {
  private static instance: TokenManager | null = null;
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private currentUser: User | null = null;

  /**
   * Obtiene la instancia singleton del TokenManager
   * Garantiza que todos los servicios compartan la misma instancia
   */
  static getInstance(): TokenManager {
    if (!TokenManager.instance) {
      TokenManager.instance = new TokenManager();
      // Cargar tokens desde localStorage al crear la instancia
      TokenManager.instance.loadTokensFromStorage();
    }
    return TokenManager.instance;
  }

  /**
   * Constructor privado para forzar el uso del patrón singleton
   */
  private constructor() {
    // Constructor privado para prevenir instanciación directa
  }

  /**
   * Carga tokens desde localStorage
   */
  loadTokensFromStorage(): void {
    this.accessToken = localStorage.getItem('access_token');
    this.refreshToken = localStorage.getItem('refresh_token');
    const userStr = localStorage.getItem('current_user');
    if (userStr) {
      try {
        this.currentUser = JSON.parse(userStr);
      } catch (e) {
        console.error('❌ [TokenManager] Error parsing user from localStorage', e);
      }
    }

    if (this.accessToken || this.refreshToken || this.currentUser) {
      authManager.setAuthData(this.accessToken, this.refreshToken, this.currentUser ?? null);
    }
  }

  /**
   * Guarda tokens en localStorage
   */
  saveTokensToStorage(accessToken: string, refreshToken: string, user: User): void {
    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
    localStorage.setItem('current_user', JSON.stringify(user));
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    this.currentUser = user;
    authManager.setAuthData(accessToken, refreshToken, user);
  }

  /**
   * Limpia tokens de localStorage
   */
  clearTokensFromStorage(): void {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('current_user');
    this.accessToken = null;
    this.refreshToken = null;
    this.currentUser = null;
    authManager.clearAuth();
  }

  /**
   * Obtiene el token de acceso actual
   */
  getAccessToken(): string | null {
    // Leer directamente de localStorage para obtener siempre el más reciente
    // Esto garantiza que todos los servicios vean el mismo token actualizado
    const storedToken = localStorage.getItem('access_token');
    if (storedToken !== this.accessToken) {
      this.accessToken = storedToken;
    }
    // Fallback a authManager si no hay en localStorage
    return this.accessToken || authManager.getToken();
  }

  /**
   * Obtiene el refresh token
   */
  getRefreshToken(): string | null {
    // Leer directamente de localStorage para obtener siempre el más reciente
    // Esto garantiza que todos los servicios vean el mismo token actualizado
    const storedRefreshToken = localStorage.getItem('refresh_token');
    if (storedRefreshToken !== this.refreshToken) {
      this.refreshToken = storedRefreshToken;
    }
    return this.refreshToken;
  }

  /**
   * Obtiene el usuario actual
   */
  getCurrentUser(): User | null {
    return this.currentUser;
  }

  /**
   * Actualiza el access token (usado después de refresh)
   */
  updateAccessToken(accessToken: string, user?: User): void {
    this.accessToken = accessToken;
    localStorage.setItem('access_token', accessToken);
    if (user) {
      this.currentUser = user;
      localStorage.setItem('current_user', JSON.stringify(user));
    }
    authManager.setAuthData(this.accessToken, this.refreshToken, this.currentUser ?? null);
  }
}
